/**
 * Main Product — Variant Picker
 *
 * Powers variant selection inside the custom product section.
 *
 * Features:
 *  - Swatch / button pickers
 *  - Dropdown pickers
 *  - Thumbnail gallery switching
 *  - URL variant syncing  (?variant=ID)
 *  - Hidden <select> syncing for form submit & theme compatibility
 */

(function () {
    "use strict";

    const jQuery = window.jQuery;
    if (!jQuery) return;

    // ─── Debug flag ───────────────────────────────────────────────────────────
    // Set to `true` while developing to log picker activity to the console.
    const DEBUG = false;

    // ─── DOM selectors ────────────────────────────────────────────────────────
    // Centralised here so any template changes only need one edit.
    const SELECTOR = {
        section: "[data-vrn]",
        variantJson: "[data-vrn-json]",
        variantSelect: "[data-vrn-variant-select]",
        price: "[data-vrn-price]",
        comparePrice: "[data-vrn-compare]",
        addToCartButton: "[data-vrn-atc]",
        pickerButton: "[data-vrn-picker]",
        pickerSelect: "[data-vrn-picker-select]",
        optionValue: "[data-vrn-option-val]",
        slide: "[data-slide]",
        thumb: "[data-vrn-thumb]",
        configButton: "[data-vrn-config-btn]",
        directBuyButton: "[data-vrn-direct-buy]",
        openGalleryButton: "[data-vrn-open-gallery]",
        closeGalleryButton: "[data-vrn-close-gallery]",
        galleryModal: "#vrGalleryModal",
        gallerySlider: "[data-vrn-gallery-slider]",
        galleryThumbsSlider: "[data-vrn-gallery-thumbs-slider]",
    };

    // Kick off once the DOM is ready.
    jQuery(initAllProductSections);

    // ─── Initialisation ───────────────────────────────────────────────────────

    /** Find every product section on the page and initialise each one. */
    function initAllProductSections() {
        jQuery(SELECTOR.section).each(function () {
            initSingleSection(jQuery(this));
        });
    }

    /**
     * Bootstrap a single product section.
     * Guards against double-initialisation via a data flag.
     *
     * @param {jQuery} $section - The product section wrapper element.
     */
    function initSingleSection($section) {
        // Skip if this section has already been set up.
        if ($section.data("bDevsInit") === true) return;
        $section.data("bDevsInit", true);

        // Read variant data embedded in the page as JSON.
        const $jsonNode = $section.find(SELECTOR.variantJson).first();
        if (!$jsonNode.length) return;

        const variantData = JSON.parse($jsonNode.text());
        let variants = variantData.variants || [];
        const moneyFormat = variantData.money_format || null;
        if (!variants.length) return;

        // Cache frequently used jQuery objects.
        const $variantSelect = $section.find(SELECTOR.variantSelect).first();
        const $price = $section.find(SELECTOR.price).first();
        const $comparePrice = $section.find(SELECTOR.comparePrice).first();
        const $addToCartButton = $section
            .find(SELECTOR.addToCartButton)
            .first();
        const $productVariantIdInput = $section.find(
            '[data-vrn-product-variant-id][name="product-variant-id"]',
        );
        const $directBuyButton = $section.find(SELECTOR.directBuyButton).first();
        const $openGalleryButton = $section.find(SELECTOR.openGalleryButton);
        const $closeGalleryButton = jQuery(SELECTOR.closeGalleryButton);
        const $galleryModal = jQuery(SELECTOR.galleryModal);
        const $gallerySlider = jQuery(SELECTOR.gallerySlider);
        const $galleryThumbsSlider = jQuery(SELECTOR.galleryThumbsSlider);

        let mainSwiperInstance = null;
        let thumbsSwiperInstance = null;

        /**
         * Tracks the currently selected option values.
         * Index 0 → Option 1 (e.g. Colour), index 1 → Option 2 (e.g. Size), etc.
         *
         * @type {string[]}
         */
        let selectedOptions = [];
        let isSyncingHiddenVariantSelect = false;

        // ─── Utilities ────────────────────────────────────────────────────────

        /** Log a message to the console only when DEBUG is enabled. */
        function logDebug(message, payload) {
            if (!DEBUG) return;
            console.log(`[vrn] ${message}`, payload || "");
        }

        /** Return the `variant` query-parameter value from the current URL. */
        function getUrlVariantId() {
            return new URLSearchParams(window.location.search).get("variant");
        }

        /**
         * Read an element attribute and parse it as an integer.
         *
         * @param   {jQuery} $element      - Target element.
         * @param   {string} attributeName - The HTML attribute to read.
         * @returns {number}
         */
        function parseIntAttr($element, attributeName) {
            return parseInt($element.attr(attributeName), 10);
        }

        /**
         * Convert a price in cents to the store's money format.
         * Uses Shopify formatter when available, with Intl fallback.
         *
         * @param   {number} cents
         * @returns {string}
         */
        function formatMoney(cents) {
            const amountInCents = Number(cents || 0);

            if (
                window.Shopify &&
                typeof window.Shopify.formatMoney === "function"
            ) {
                // Force explicit decimal format ${{amount}} ($228.00) for accurate currency app conversion
                return window.Shopify.formatMoney(
                    amountInCents,
                    "${{amount}}",
                );
            }

            try {
                return new Intl.NumberFormat("en-US", {
                    style: "currency",
                    currency: "USD",
                }).format(amountInCents / 100);
            } catch (error) {
                return "$" + (amountInCents / 100).toFixed(2);
            }
        }

        // ─── Variant lookup ───────────────────────────────────────────────────

        /**
         * Find a variant by its numeric or string ID.
         *
         * @param   {string|number} variantId
         * @returns {object|null}
         */
        function findVariantById(variantId) {
            if (!variantId) return null;
            return (
                variants.find(
                    (variant) => String(variant.id) === String(variantId),
                ) || null
            );
        }

        /**
         * Find the first variant whose options array matches the supplied array exactly.
         *
         * @param   {string[]} options - e.g. ['Red', 'Large']
         * @returns {object|null}
         */
        function findVariantByOptions(options) {
            return (
                variants.find((variant) =>
                    variant.options.every(
                        (value, index) => value === options[index],
                    ),
                ) || null
            );
        }

        /**
         * Determine which variant to show on first load, using the following priority:
         *  1. Variant ID set directly on the section element
         *  2. Variant ID in the URL query string
         *  3. Variant ID in the hidden <select> element
         *  4. First variant in the list
         *
         * @returns {object}
         */
        function getInitialVariant() {
            const sectionVariantId = $section.attr("data-initial-variant-id");
            const sectionVariant = findVariantById(sectionVariantId);
            if (sectionVariant) return sectionVariant;

            const urlVariant = findVariantById(getUrlVariantId());
            if (urlVariant) return urlVariant;

            const hiddenSelectVariant = findVariantById($variantSelect.val());
            if (hiddenSelectVariant) return hiddenSelectVariant;

            return variants[0];
        }

        // ─── Gallery helpers ──────────────────────────────────────────────────

        /**
         * Collect all media / image IDs that are associated with a variant.
         * Shopify exposes this through several different keys depending on the
         * theme setup, so we try every known location and drop any blanks.
         *
         * @param   {object} variant
         * @returns {string[]}
         */
        function getVariantImageIds(variant) {
            return [
                variant?.featured_media?.id,
                variant?.featured_media?.preview_image?.id,
                variant?.featured_image?.id,
                variant?.featured_image?.media_id,
                variant?.image_id,
            ]
                .filter(Boolean)
                .map(String);
        }


        // variant indexes find // add new
        // function findVariantSlideIndex(variant) {
        //     const $slides = $section.find('.swiper-slide[data-slide]');
        //     if (!$slides.length) return 0;

        //     const variantImageIds = getVariantImageIds(variant);
        //     if (!variantImageIds.length) return 0;

        //     let matchedIndex = 0;

        //     $slides.each(function (index, slide) {
        //         const $slide = jQuery(slide);
        //         const mediaId = String($slide.attr("data-media-id") || "");
        //         const imageId = String($slide.attr("data-image-id") || "");
        //         const isMatch = 
        //             variantImageIds.includes(mediaId) ||
        //             variantImageIds.includes(imageId);

        //         if (isMatch) {
        //             matchedIndex = index;
        //             return false; // break the loop
        //         }
        //     });

        //     return matchedIndex;
        // }


        /**
 * Find the slide index that matches the variant's image
 * If no match found, return 0 (first slide) or find the first variant image
 */
        function findVariantSlideIndex(variant) {
            const $slides = $section.find('.swiper-slide[data-slide]');
            if (!$slides.length) return 0;

            const variantImageIds = getVariantImageIds(variant);

            // If variant has no images, return 0 (first slide)
            if (!variantImageIds.length) {
                // Optional: Find first variant that has an image
                return findFirstVariantImageSlide();
            }

            let matchedIndex = 0;

            $slides.each(function (index, slide) {
                const $slide = jQuery(slide);
                const mediaId = String($slide.attr("data-media-id") || "");
                const imageId = String($slide.attr("data-image-id") || "");
                const isMatch =
                    variantImageIds.includes(mediaId) ||
                    variantImageIds.includes(imageId);

                if (isMatch) {
                    matchedIndex = index;
                    return false; // break the loop
                }
            });

            return matchedIndex;
        }

        /**
         * Find the first slide that belongs to any variant
         * Falls back to slide 0
         */
        function findFirstVariantImageSlide() {
            const $slides = $section.find('.swiper-slide[data-slide]');

            // Check each slide if it has a variant associated
            for (let i = 0; i < $slides.length; i++) {
                const $slide = $slides.eq(i);
                const mediaId = String($slide.attr("data-media-id") || "");
                const imageId = String($slide.attr("data-image-id") || "");

                // Check if this image belongs to any variant
                const hasVariant = variants.some(variant => {
                    const variantImageIds = getVariantImageIds(variant);
                    return variantImageIds.includes(mediaId) || variantImageIds.includes(imageId);
                });

                if (hasVariant) {
                    return i;
                }
            }

            // Fallback to first slide
            return 0;
        }

        /**
         * Find the variant associated with a given slide index, if any.
         * Returns the matching variant object, or null if no variant matches this slide.
         *
         * @param   {number} slideIndex
         * @returns {object|null}
         */
        function findVariantBySlideIndex(slideIndex) {
            const $slides = $section.find('.swiper-slide[data-slide]');
            if (!$slides.length || slideIndex < 0 || slideIndex >= $slides.length) return null;

            const $targetSlide = $slides.eq(slideIndex);
            const mediaId = String($targetSlide.attr("data-media-id") || "");
            const imageId = String($targetSlide.attr("data-image-id") || "");

            if (!mediaId && !imageId) return null;

            return (
                variants.find((variant) => {
                    const variantImageIds = getVariantImageIds(variant);
                    return (
                        (mediaId && variantImageIds.includes(mediaId)) ||
                        (imageId && variantImageIds.includes(imageId))
                    );
                }) || null
            );
        }

        // ─── DOM sync helpers ─────────────────────────────────────────────────

        /**
         * Push the selected variant ID into the browser's address bar
         * without triggering a full page reload.
         *
         * @param {object} variant
         */
        function syncUrlVariant(variant) {
            const url = new URL(window.location.href);
            const current = url.searchParams.get("variant");
            const next = String(variant.id);

            if (current === next) return; // Nothing to update.

            url.searchParams.set("variant", next);
            window.history.replaceState({}, "", url.toString());
        }

        /**
         * Keep the hidden native <select> in sync so the add-to-cart form
         * submits the correct variant and other theme scripts stay compatible.
         *
         * @param {object} variant
         */
        function syncHiddenVariantSelect(variant) {
            if (!$variantSelect.length) return;

            const variantId = String(variant.id);
            const currentVariantId = String($variantSelect.val() || "");

            if (currentVariantId === variantId) return;

            isSyncingHiddenVariantSelect = true;
            $variantSelect.val(variantId);

            // Explicitly mark the matching <option> as selected for older browsers.
            $variantSelect
                .find(`option[value="${variantId}"]`)
                .prop("selected", true);

            $variantSelect.trigger("change");
            isSyncingHiddenVariantSelect = false;
        }

        /**
         * Keep hidden `product-variant-id` input synced with current variant.
         *
         * @param {object} variant
         */
        function syncHiddenProductVariantIdInput(variant) {
            if (!$productVariantIdInput.length) return;
            $productVariantIdInput.val(String(variant.id));
        }

        /**
         * Refresh the displayed price and compare-at price for the chosen variant.
         *
         * @param {object} variant
         */
        function convertAndFormatPrice(priceCents, formattedUsdString) {
            var cleanPrice = (formattedUsdString || '').replace(/<[^>]*>?/gm, '').trim();
            // Remove trailing .00 / ,00 (keeps real cents like .50)
            cleanPrice = cleanPrice.replace(/[.,]00(?=\D*$)/, '');
            return '<span class="money">' + cleanPrice + '</span>';
        }
        function updatePriceBlock(variant) {
            if (!variant) return;

            const $currentTitle = $section.find(".b-devs__title").first();
            const $currentPrice = $section.find(SELECTOR.price).first();
            const $currentComparePrice = $section.find(SELECTOR.comparePrice).first();
            const $floatTitle = $section.find("[data-vrn-float-title], .float-pd-info .info-content h3").first();
            const $floatPrice = $section.find("[data-vrn-float-price], .float-pd-info .info-content p").first();
            const $floatImg = $section.find("[data-vrn-float-img], .float-pd-info img").first();

            if ($currentTitle.length && $floatTitle.length) {
                $floatTitle.html($currentTitle.html());
            }

            var rawPrice = Math.round(variant.price);
            var formattedUsd = formatMoney(rawPrice);
            var convertedHtml = convertAndFormatPrice(rawPrice, formattedUsd);

            if ($currentPrice.length) {
                $currentPrice.html(convertedHtml);
            }

            if ($floatPrice.length) {
                $floatPrice.html(convertedHtml);
            }

            if ($floatImg.length) {
                const variantImgSrc =
                    variant?.featured_image?.src ||
                    variant?.featured_media?.preview_image?.src;
                if (variantImgSrc) {
                    $floatImg.attr("src", variantImgSrc);
                }
            }

            if ($currentComparePrice.length) {
                const hasComparePrice = variant.compare_at_price > variant.price;
                $currentComparePrice.css("display", hasComparePrice ? "" : "none");

                if (hasComparePrice) {
                    var compareRaw = Math.round(variant.compare_at_price);
                    var compareUsd = formatMoney(compareRaw);
                    var compareConvertedHtml = convertAndFormatPrice(compareRaw, compareUsd);
                    $currentComparePrice.html(compareConvertedHtml);
                }
            }

            const triggerAppConversion = function () {
                try {
                    if (window.bucksCC && typeof window.bucksCC.convert === 'function') {
                        window.bucksCC.convert();
                    } else if (window.bucks && typeof window.bucks.convert === 'function') {
                        window.bucks.convert();
                    }
                } catch (err) { }
                document.dispatchEvent(new CustomEvent('currency:updated'));
                document.dispatchEvent(new CustomEvent('currency:change'));
                jQuery(document.body).trigger('currency:updated');
            };

            // Immediate synchronous conversion (prevents flicker before browser paint)
            triggerAppConversion();

            // Secondary frame tick check for deferred scripts
            if (window.requestAnimationFrame) {
                window.requestAnimationFrame(triggerAppConversion);
            }
        }

        /**
         * Disable or re-enable the add-to-cart button and update its label
         * to reflect the variant's availability.
         *
         * @param {object} variant
         */
        function updateAddToCartBlock(variant) {
            if (!$addToCartButton.length) return;

            const isAvailable = Boolean(variant && variant.available);

            $addToCartButton.prop("disabled", !isAvailable);
            if ($directBuyButton.length) {
                $directBuyButton.prop("disabled", !isAvailable);
            }

            if (isAvailable) {
                $addToCartButton.removeAttr("disabled");
                if ($directBuyButton.length) {
                    $directBuyButton.removeAttr("disabled");
                }
            } else {
                $addToCartButton.attr("disabled", "disabled");
                if ($directBuyButton.length) {
                    $directBuyButton.attr("disabled", "disabled");
                }
            }

            $addToCartButton.text(
                isAvailable
                    ? $addToCartButton.data("addLabel")
                    : $addToCartButton.data("unavailableLabel"),
            );
        }

        /**
         * Update any inline text nodes that display the current value of an option
         * (e.g. "Colour: Red" labels next to a swatch picker).
         */
        function syncOptionValueText() {
            $section.find(SELECTOR.optionValue).each(function () {
                const $label = jQuery(this);
                const optionIndex = parseIntAttr(
                    $label,
                    "data-vrn-option-val",
                );
                if (isNaN(optionIndex)) return;

                $label.text(selectedOptions[optionIndex]);
            });
        }

        /**
         * Mark one gallery slide (and its matching thumbnail) as active,
         * removing the active state from all others.
         *
         * @param {jQuery} $slides      - All slide elements.
         * @param {jQuery} $thumbs      - All thumbnail elements.
         * @param {number} activeIndex  - Zero-based index of the slide to show.
         */
        function setActiveGalleryIndex($slides, $thumbs, activeIndex) {
            $slides.each(function (index, slide) {
                console.log(index);
                const isActive = index === activeIndex;
                const $slide = jQuery(slide);
                const $thumb = $thumbs.eq(index);

                $slide.toggleClass("is-active", isActive);

                if ($thumb.length) {
                    $thumb.toggleClass("is-active", isActive);
                    $thumb.attr("aria-selected", isActive ? "true" : "false");
                }
            });
        }

        /**
         * When the variant changes, scroll / snap the gallery to the slide that
         * shows the variant's featured image.  Falls back to slide 0 if no match.
         *
         * @param {object} variant
         */
        function syncGalleryForVariant(variant) {
            if (!variant) return;

            resetInlineZoom();

            const slideIndex = findVariantSlideIndex(variant);

            // Update main slider using Swiper API
            if (window.mainProductSwiper) {
                window.mainProductSwiper.slideTo(slideIndex, 300);
            }

            // Update thumbnail slider using Swiper API
            if (window.mainThumbSwiper) {
                window.mainThumbSwiper.slideTo(slideIndex, 300);
            }

            // Update active states
            const $slides = $section.find('.swiper-slide[data-slide]');
            const $thumbs = $section.find(SELECTOR.thumb);

            // Remove all active classes
            $slides.removeClass('is-active');
            $thumbs.removeClass('is-active');
            $thumbs.removeClass('thumb-active');
            $thumbs.removeClass('swiper-slide-thumb-active');

            // Add active class to current
            $slides.eq(slideIndex).addClass('is-active');
            $thumbs.eq(slideIndex).addClass('is-active');
            $thumbs.eq(slideIndex).addClass('thumb-active');
        }

        /**
         * Reflect the current selectedOptions array in all picker controls:
         *  - Toggle `is-selected` on swatch / button pickers.
         *  - Set the value of dropdown pickers.
         */
        function syncPickerControls() {
            // Swatch / button pickers.
            $section.find(SELECTOR.pickerButton).each(function () {
                const $button = jQuery(this);
                const optionIndex = parseIntAttr($button, "data-option-index");
                const buttonValue = $button.attr("data-vrn-picker");
                const isSelected = buttonValue === selectedOptions[optionIndex];

                $button.toggleClass("is-selected", isSelected);
            });

            // Dropdown pickers.
            $section.find(SELECTOR.pickerSelect).each(function () {
                const $select = jQuery(this);
                const optionIndex = parseIntAttr($select, "data-option-index");
                $select.val(selectedOptions[optionIndex]);
            });
        }

        // ─── Core render ──────────────────────────────────────────────────────

        /**
         * Apply all UI updates for a given variant in one call.
         * This is the single function that should be called after any variant change.
         *
         * @param {object|null} variant
         * @param {object}      [options={}]
         * @param {boolean}     [options.skipGallerySlide=false]
         */
        function renderVariant(variant, options = {}) {
            if (!variant) return;

            updatePriceBlock(variant);
            updateAddToCartBlock(variant);
            syncHiddenVariantSelect(variant);
            syncHiddenProductVariantIdInput(variant);
            syncUrlVariant(variant);
            if (!options.skipGallerySlide) {
                syncGalleryForVariant(variant);
            }
            syncOptionValueText();
        }

        // ─── Variant resolution logic ─────────────────────────────────────────

        /**
         * After the shopper picks a new option value, work out which variant
         * should now be active.
         *
         * Resolution order:
         *  1. Exact match on all current selectedOptions.
         *  2. An available variant that at least matches the changed option.
         *  3. Any variant that matches the changed option (sold out is OK).
         *
         * When a fallback is used, selectedOptions is updated and the picker
         * controls are re-synced so the UI stays consistent.
         *
         * @param   {number} optionIndex  - Which option position changed (0-based).
         * @param   {string} optionValue  - The new value chosen.
         * @returns {object|null}
         */
        function resolveVariantAfterSelection(optionIndex, optionValue) {
            selectedOptions[optionIndex] = optionValue;

            // 1. Perfect match.
            const exactMatch = findVariantByOptions(selectedOptions);
            if (exactMatch) return exactMatch;

            // 2. Same option, but prefer an available variant.
            const availableFallback = variants.find(
                (variant) =>
                    variant.options[optionIndex] === optionValue &&
                    variant.available,
            );
            if (availableFallback) {
                selectedOptions = availableFallback.options.slice();
                syncPickerControls();
                return availableFallback;
            }

            // 3. Same option, availability doesn't matter.
            const firstFallback = variants.find(
                (variant) => variant.options[optionIndex] === optionValue,
            );
            if (firstFallback) {
                selectedOptions = firstFallback.options.slice();
                syncPickerControls();
                return firstFallback;
            }

            return null;
        }

        /**
         * Set a variant as the active one, updating both the UI and internal state.
         *
         * @param {object|null} variant
         * @param {object}      [options={}]
         */
        function applyVariant(variant, options = {}) {
            if (!variant) return;
            selectedOptions = variant.options.slice();
            syncPickerControls();
            renderVariant(variant, options);
        }

        // ─── Event handlers ───────────────────────────────────────────────────

        /** Handle clicks on swatch / button pickers. */
        function onPickerButtonClick() {
            const $button = jQuery(this);
            const optionIndex = parseIntAttr($button, "data-option-index");
            const optionValue = $button.attr("data-vrn-picker");

            logDebug("picker button click", {
                optionIndex,
                optionValue,
                element: this,
            });

            // Deselect all buttons for this option, then select the clicked one.
            $section
                .find(
                    `${SELECTOR.pickerButton}[data-option-index="${optionIndex}"]`,
                )
                .removeClass("is-selected");
            $button.addClass("is-selected");

            const nextVariant = resolveVariantAfterSelection(
                optionIndex,
                optionValue,
            );
            renderVariant(nextVariant);
        }

        /** Handle changes to dropdown pickers. */
        function onPickerSelectChange() {
            const $select = jQuery(this);
            const optionIndex = parseIntAttr($select, "data-option-index");
            const optionValue = $select.val();

            logDebug("picker select change", {
                optionIndex,
                optionValue,
                element: this,
            });

            const nextVariant = resolveVariantAfterSelection(
                optionIndex,
                optionValue,
            );
            renderVariant(nextVariant);
        }

        /**
         * Switch between linked products via Shopify Section Rendering API without page reload.
         *
         * @param {string} targetUrl - URL of the product to switch to.
         * @param {boolean} [updateHistory=true] - Whether to push state to history.
         */
        function switchConfigurationProduct(targetUrl, updateHistory = true) {
            if (!targetUrl) return;

            const sectionId = $section.attr("data-section-id");
            if (!sectionId) return;

            // Mark section as loading / transitioning
            $section.addClass("is-config-loading");

            // Format URL for Section Rendering API
            const separator = targetUrl.indexOf("?") > -1 ? "&" : "?";
            const fetchUrl = `${targetUrl}${separator}section_id=${sectionId}`;

            fetch(fetchUrl)
                .then(function (response) {
                    if (!response.ok) throw new Error("Network response was not ok");
                    return response.text();
                })
                .then(function (html) {
                    const parser = new DOMParser();
                    const doc = parser.parseFromString(html, "text/html");
                    const $newSection = jQuery(doc).find(`[data-section-id="${sectionId}"]`);

                    if (!$newSection.length) {
                        window.location.href = targetUrl;
                        return;
                    }

                    // 1. Swap Media Gallery (Sliders & Thumbnails)
                    const $newMedia = $newSection.find(".b-devs__media");
                    if ($newMedia.length) {
                        $section.find(".b-devs__media").html($newMedia.html());
                    }

                    // 2. Swap Fullscreen Modal Gallery content & reset modal swiper instances
                    const $newModal = jQuery(doc).find(SELECTOR.galleryModal);
                    if ($newModal.length) {
                        $galleryModal.html($newModal.html());
                        if (mainSwiperInstance) {
                            try { mainSwiperInstance.destroy(true, true); } catch (e) { }
                            mainSwiperInstance = null;
                        }
                        if (thumbsSwiperInstance) {
                            try { thumbsSwiperInstance.destroy(true, true); } catch (e) { }
                            thumbsSwiperInstance = null;
                        }
                    }

                    // 3. Swap Product Title (Main + Floating Info)
                    const $newTitle = $newSection.find(".b-devs__title");
                    if ($newTitle.length) {
                        $section.find(".b-devs__title").html($newTitle.html());
                        $section.find(".float-pd-info h3, [data-vrn-float-title]").html($newTitle.html());
                    }

                    // 4. Swap Price Row (Main + Floating Info)
                    const $newPriceRow = $newSection.find(".b-devs__price-row");
                    if ($newPriceRow.length) {
                        $section.find(".b-devs__price-row").html($newPriceRow.html());
                        const $newMainPrice = $newPriceRow.find(SELECTOR.price).first();
                        if ($newMainPrice.length) {
                            $section.find(".float-pd-info p, [data-vrn-float-price]").html($newMainPrice.html());
                        }
                    }

                    // 4b. Swap Floating Featured Image from new media gallery
                    const $newFeaturedImg = $newSection.find(".float-pd-info img, .b-devs__main-img img").first();
                    if ($newFeaturedImg.length && $newFeaturedImg.attr("src")) {
                        $section.find(".float-pd-info img, [data-vrn-float-img]").attr("src", $newFeaturedImg.attr("src"));
                    }

                    // 5. Swap Configuration Switcher Swatches
                    const $newConfigSwitcher = $newSection.find(".b-devs__config-switcher");
                    if ($newConfigSwitcher.length) {
                        $section.find(".b-devs__config-switcher").html($newConfigSwitcher.html());
                    }

                    // 6. Swap Hidden Variant Select & Hidden Variant ID
                    const $newSelect = $newSection.find(SELECTOR.variantSelect);
                    if ($newSelect.length) {
                        $section.find(SELECTOR.variantSelect).html($newSelect.html());
                    }

                    const $newVariantId = $newSection.find('[data-vrn-product-variant-id]');
                    if ($newVariantId.length) {
                        $section.find('[data-vrn-product-variant-id]').val($newVariantId.val());
                    }

                    // 7. Swap ATC Button state & availability
                    const $newAtc = $newSection.find(SELECTOR.addToCartButton);
                    if ($newAtc.length) {
                        const $currentAtc = $section.find(SELECTOR.addToCartButton);
                        $currentAtc.text($newAtc.text());
                        $currentAtc.attr("data-add-label", $newAtc.attr("data-add-label") || "");
                        $currentAtc.attr("data-unavailable-label", $newAtc.attr("data-unavailable-label") || "");
                        $currentAtc.prop("disabled", $newAtc.prop("disabled"));
                    }

                    // 8. Swap Excerpt and Description
                    const $newExcerpt = $newSection.find(".product-excerpt");
                    if ($newExcerpt.length) {
                        $section.find(".product-excerpt").html($newExcerpt.html());
                    }
                    const $newDesc = $newSection.find(".product-description");
                    if ($newDesc.length) {
                        $section.find(".product-description").html($newDesc.html());
                    }

                    // 9. Swap Variant JSON & update internal variants array
                    const $newJson = $newSection.find(SELECTOR.variantJson);
                    if ($newJson.length) {
                        $section.find(SELECTOR.variantJson).text($newJson.text());
                        try {
                            const newVariantData = JSON.parse($newJson.text());
                            if (newVariantData.variants) {
                                variants = newVariantData.variants;
                                if (variants[0]) {
                                    updatePriceBlock(variants[0]);
                                }
                            }
                        } catch (e) { }
                    }

                    // 9b. Trigger currency conversion for newly swapped price & floating info
                    try {
                        if (window.bucksCC && typeof window.bucksCC.convert === "function") {
                            window.bucksCC.convert();
                        } else if (window.bucks && typeof window.bucks.convert === "function") {
                            window.bucks.convert();
                        }
                    } catch (e) { }
                    document.dispatchEvent(new CustomEvent("currency:updated"));
                    document.dispatchEvent(new CustomEvent("currency:change"));
                    jQuery(document.body).trigger("currency:updated");

                    // 10. Update browser URL without reload
                    if (updateHistory) {
                        window.history.pushState({ path: targetUrl }, "", targetUrl);
                    }

                    // 11. Re-initialize Swiper gallery sliders for the new product's images
                    initSwiperSliders();

                    // 12. Fade smoothly back in
                    $section.removeClass("is-config-loading");
                })
                .catch(function (err) {
                    console.error("[vrn] Configuration Switch error:", err);
                    $section.removeClass("is-config-loading");
                    window.location.href = targetUrl;
                });
        }

        /** Handle click on configuration swatch button */
        function onConfigButtonClick(e) {
            e.preventDefault();
            const $button = jQuery(this);
            if ($button.hasClass("is-selected")) return;

            const targetUrl = $button.attr("data-product-url");
            if (!targetUrl) return;

            // Highlight immediately for instantaneous feedback
            $section.find(SELECTOR.configButton).removeClass("is-selected");
            $button.addClass("is-selected");

            switchConfigurationProduct(targetUrl, true);
        }

        // ─── In-Place Main Image Zoom & Drag (Mobile) ─────────────────────────
        let inlineZoomScale = 1;
        let inlinePanX = 0;
        let inlinePanY = 0;
        let isInlinePanning = false;
        let inlineStartX = 0;
        let inlineStartY = 0;

        function getActiveMainSlideImage() {
            let $activeSlide = $section.find('.b-devs__main-slider .swiper-slide-active');
            if (!$activeSlide.length) {
                $activeSlide = $section.find('.b-devs__main-slider .b-devs__main-img.is-active');
            }
            if (!$activeSlide.length) {
                $activeSlide = $section.find('.b-devs__main-slider .b-devs__main-img').first();
            }
            return $activeSlide.find('img').first();
        }

        function clampInlinePan($img) {
            const $slide = $img.closest('.b-devs__main-img');
            const width = $slide.outerWidth() || $img.outerWidth() || 300;
            const height = $slide.outerHeight() || $img.outerHeight() || 300;

            const maxPanX = Math.max(0, (width * (inlineZoomScale - 1)) / 2);
            const maxPanY = Math.max(0, (height * (inlineZoomScale - 1)) / 2);

            inlinePanX = Math.min(maxPanX, Math.max(-maxPanX, inlinePanX));
            inlinePanY = Math.min(maxPanY, Math.max(-maxPanY, inlinePanY));
        }

        function applyInlineZoomTransform() {
            const $mainSlider = $section.find('[data-vrn-main-slider]');
            const $zoomBtn = $section.find('[data-vrn-inline-zoom]');
            const $img = getActiveMainSlideImage();

            if (!$img.length) return;

            if (inlineZoomScale <= 1) {
                inlineZoomScale = 1;
                inlinePanX = 0;
                inlinePanY = 0;
                $section.find('.b-devs__main-slider .b-devs__main-img img').css(
                    'transform',
                    'translate3d(0px, 0px, 0px) scale(1)'
                );
                $mainSlider.removeClass('is-zoomed is-dragging');
                $zoomBtn.removeClass('is-zoomed');
                if (window.mainProductSwiper) {
                    window.mainProductSwiper.allowTouchMove = true;
                }
            } else {
                clampInlinePan($img);
                $img.css(
                    'transform',
                    `translate3d(${inlinePanX}px, ${inlinePanY}px, 0px) scale(${inlineZoomScale})`
                );
                $mainSlider.addClass('is-zoomed');
                $zoomBtn.addClass('is-zoomed');
                if (window.mainProductSwiper) {
                    window.mainProductSwiper.allowTouchMove = false;
                }
            }
        }

        function resetInlineZoom() {
            if (inlineZoomScale === 1 && inlinePanX === 0 && inlinePanY === 0) return;
            inlineZoomScale = 1;
            inlinePanX = 0;
            inlinePanY = 0;
            isInlinePanning = false;
            applyInlineZoomTransform();
        }

        function toggleInlineZoom(e) {
            if (e) {
                e.preventDefault();
                e.stopPropagation();
            }
            if (inlineZoomScale > 1) {
                resetInlineZoom();
            } else {
                inlineZoomScale = 2.2;
                inlinePanX = 0;
                inlinePanY = 0;
                applyInlineZoomTransform();
            }
        }

        /** Update UI and reverse variant lookup when a gallery slide is selected. */
        function handleSlideSelection(activeIndex) {
            if (isNaN(activeIndex)) return;

            resetInlineZoom();

            const $slides = $section.find('.swiper-slide[data-slide]');
            const $thumbs = $section.find(SELECTOR.thumb);
            const $thumbItems = $section.find('.thumb-item');

            // Manage active slide state
            $slides.removeClass('is-active');
            $slides.eq(activeIndex).addClass('is-active');

            // Manage active thumbnail state
            $thumbs.removeClass('is-active');
            $thumbs.removeClass('thumb-active');
            $thumbItems.removeClass('swiper-slide-thumb-active');

            const $currentThumb = $thumbs.eq(activeIndex);
            if ($currentThumb.length) {
                $currentThumb.addClass('is-active');
                $currentThumb.addClass('thumb-active');
            }

            const $currentThumbItem = $thumbItems.eq(activeIndex);
            if ($currentThumbItem.length) {
                $currentThumbItem.addClass('swiper-slide-thumb-active');
            }

            $thumbs.each(function (index) {
                jQuery(this).attr('aria-selected', index === activeIndex ? 'true' : 'false');
            });

            // Reverse lookup: check if the selected thumbnail/slide corresponds to a variant
            const matchingVariant = findVariantBySlideIndex(activeIndex);
            if (matchingVariant) {
                applyVariant(matchingVariant, { skipGallerySlide: true });
            }
        }

        /** Handle clicks on gallery thumbnails to manually switch the active slide. */
        function onThumbClick() {
            const $thumb = jQuery(this);
            const activeIndex = parseIntAttr($thumb, "data-vrn-thumb");

            if (isNaN(activeIndex)) return;

            resetInlineZoom();

            if (window.mainProductSwiper) {
                window.mainProductSwiper.slideTo(activeIndex, 300);
            }
            if (window.mainThumbSwiper) {
                window.mainThumbSwiper.slideTo(activeIndex, 300);
            }

            handleSlideSelection(activeIndex);
        }

        let currentZoomScale = 1;
        let panX = 0;
        let panY = 0;
        let isPanning = false;
        let startPanX = 0;
        let startPanY = 0;

        function applyTransformToActiveModalImage() {
            const $activeImg = $galleryModal.find('.vr-gallery-modal__slide.swiper-slide-active .vr-gallery-modal__image');
            const $zoomLabel = $galleryModal.find('[data-vrn-zoom-reset]');

            if ($zoomLabel.length) {
                $zoomLabel.text(Math.round(currentZoomScale * 100) + '%');
            }

            if (!$activeImg.length) return;

            if (currentZoomScale <= 1) {
                panX = 0;
                panY = 0;
                $activeImg.css('transform', 'scale(1) translate3d(0px, 0px, 0px)');
                if (mainSwiperInstance) mainSwiperInstance.allowTouchMove = true;
            } else {
                $activeImg.css(
                    'transform',
                    `translate3d(${panX}px, ${panY}px, 0px) scale(${currentZoomScale})`
                );
                if (mainSwiperInstance) mainSwiperInstance.allowTouchMove = false;
            }
        }

        function setZoomScale(nextScale) {
            currentZoomScale = Math.min(3, Math.max(1, Number(nextScale.toFixed(2))));
            if (currentZoomScale === 1) {
                panX = 0;
                panY = 0;
            }
            applyTransformToActiveModalImage();
        }

        function resetAllModalImagesZoom() {
            currentZoomScale = 1;
            panX = 0;
            panY = 0;
            $galleryModal.find('.vr-gallery-modal__image').css('transform', 'scale(1) translate3d(0px, 0px, 0px)');
            $galleryModal.find('[data-vrn-zoom-reset]').text('100%');
            if (mainSwiperInstance) mainSwiperInstance.allowTouchMove = true;
        }

        function openGallery(e) {
            if (e) e.preventDefault();

            const initialIndex = window.mainProductSwiper
                ? window.mainProductSwiper.activeIndex
                : ($section.find(SELECTOR.slide + '.is-active').index() || 0);

            $galleryModal.addClass('is-active').attr('aria-hidden', 'false').show();
            jQuery('body').addClass('vr-gallery-open');

            if (!mainSwiperInstance && typeof Swiper !== "undefined") {
                thumbsSwiperInstance = new Swiper(SELECTOR.galleryThumbsSlider, {
                    spaceBetween: 12,
                    slidesPerView: 'auto',
                    freeMode: true,
                    watchSlidesProgress: true,
                });

                mainSwiperInstance = new Swiper(SELECTOR.gallerySlider, {
                    spaceBetween: 10,
                    zoom: {
                        maxRatio: 3,
                        minRatio: 1,
                        toggle: true,
                    },
                    navigation: {
                        nextEl: SELECTOR.galleryModal + ' [data-vrn-gallery-next]',
                        prevEl: SELECTOR.galleryModal + ' [data-vrn-gallery-prev]',
                    },
                    thumbs: {
                        swiper: thumbsSwiperInstance,
                    },
                    keyboard: {
                        enabled: true,
                    },
                    on: {
                        slideChange: function () {
                            resetAllModalImagesZoom();
                        }
                    }
                });
            }

            if (mainSwiperInstance) {
                if (thumbsSwiperInstance) {
                    thumbsSwiperInstance.update();
                }
                mainSwiperInstance.update();
                mainSwiperInstance.slideTo(initialIndex, 0);
            }

            resetAllModalImagesZoom();
            setTimeout(function () {
                setZoomScale(1.5);
            }, 60);
        }

        function closeGallery() {
            resetAllModalImagesZoom();
            $galleryModal.removeClass('is-active').attr('aria-hidden', 'true').hide();
            jQuery('body').removeClass('vr-gallery-open');
        }

        // ─── Event binding ────────────────────────────────────────────────────

        /** Attach all event listeners for this section. */

        // Initialize Swiper sliders
        // ─── Bootstrap ────────────────────────────────────────────────────────

        // Initialize Swiper sliders with thumbs connection
        function initSwiperSliders() {
            const $thumbSlider = $section.find('[data-vrn-thumb-slider]');
            const $mainSlider = $section.find('[data-vrn-main-slider]');

            if (typeof Swiper === "undefined") return;

            // Destroy existing instances if present to prevent double-initialization breaking slide sync
            if ($thumbSlider[0] && $thumbSlider[0].swiper) {
                $thumbSlider[0].swiper.destroy(true, true);
            }
            if ($mainSlider[0] && $mainSlider[0].swiper) {
                $mainSlider[0].swiper.destroy(true, true);
            }

            let thumbSwiper = null;

            // 1. Initialize Thumbnail Swiper first (without mounting/rendering loops yet)
            if ($thumbSlider.length) {
                thumbSwiper = new Swiper($thumbSlider[0], {
                    spaceBetween: 6,
                    slidesPerView: 4,
                    centeredSlides: false,
                    centeredSlidesBounds: false,
                    slideToClickedSlide: true,
                    watchSlidesProgress: true,
                    freeMode: true,
                    normalizeSlideIndex: true,
                    breakpoints: {
                        480: {
                            slidesPerView: 5,
                            spaceBetween: 6,
                        },
                        768: {
                            slidesPerView: 6,
                            spaceBetween: 10,
                        }
                    }
                });
            }

            // 2. Initialize Main Swiper and bind thumbs immediately
            if ($mainSlider.length) {
                window.mainProductSwiper = new Swiper($mainSlider[0], {
                    slidesPerView: 1,
                    spaceBetween: 0,
                    effect: 'slide',
                    speed: 300,
                    watchSlidesProgress: false,
                    autoHeight: true,
                    allowTouchMove: true,
                    thumbs: {
                        swiper: thumbSwiper,
                        autoScrollOffset: 1,
                    },
                    on: {
                        slideChange: function () {
                            handleSlideSelection(this.activeIndex);
                        }
                    }
                });
            }

            // 3. Store reference for later use
            window.mainThumbSwiper = thumbSwiper;

            // 4. Force a single update to settle layout and prevent initial jumping/scrolling
            if (thumbSwiper) thumbSwiper.update();
            if (window.mainProductSwiper) window.mainProductSwiper.update();
        }


        function bindEvents() {
            $section.on("click", SELECTOR.pickerButton, onPickerButtonClick);
            $section.on("change", SELECTOR.pickerSelect, onPickerSelectChange);
            $section.on("click", SELECTOR.configButton, onConfigButtonClick);

            // Use the onThumbClick function
            $section.on("click", SELECTOR.thumb, onThumbClick);

            // In-place mobile zoom toggle & drag/pinch pan on main slider
            $section.on("click", "[data-vrn-inline-zoom]", toggleInlineZoom);

            let inlinePinchDist = null;
            let inlinePinchStartScale = 1;

            function calcTouchDist(touches) {
                const dx = touches[0].clientX - touches[1].clientX;
                const dy = touches[0].clientY - touches[1].clientY;
                return Math.hypot(dx, dy);
            }

            $section.on("touchstart", "[data-vrn-main-slider]", function (e) {
                if (jQuery(e.target).closest("[data-vrn-inline-zoom]").length) return;
                const touches = e.originalEvent.touches;
                if (touches && touches.length === 2) {
                    isInlinePanning = false;
                    inlinePinchDist = calcTouchDist(touches);
                    inlinePinchStartScale = inlineZoomScale;
                    $section.find("[data-vrn-main-slider]").addClass("is-dragging");
                } else if (touches && touches.length === 1 && inlineZoomScale > 1) {
                    isInlinePanning = true;
                    inlineStartX = touches[0].clientX - inlinePanX;
                    inlineStartY = touches[0].clientY - inlinePanY;
                    $section.find("[data-vrn-main-slider]").addClass("is-dragging");
                }
            });

            $section.on("touchmove", "[data-vrn-main-slider]", function (e) {
                const touches = e.originalEvent.touches;
                if (touches && touches.length === 2 && inlinePinchDist) {
                    e.preventDefault();
                    const newDist = calcTouchDist(touches);
                    inlineZoomScale = Math.min(3, Math.max(1, Number((inlinePinchStartScale * (newDist / inlinePinchDist)).toFixed(2))));
                    applyInlineZoomTransform();
                } else if (touches && touches.length === 1 && isInlinePanning && inlineZoomScale > 1) {
                    e.preventDefault();
                    inlinePanX = touches[0].clientX - inlineStartX;
                    inlinePanY = touches[0].clientY - inlineStartY;
                    applyInlineZoomTransform();
                }
            });

            $section.on("touchend touchcancel", "[data-vrn-main-slider]", function () {
                isInlinePanning = false;
                inlinePinchDist = null;
                $section.find("[data-vrn-main-slider]").removeClass("is-dragging");
            });

            $section.on("mousedown", "[data-vrn-main-slider]", function (e) {
                if (inlineZoomScale <= 1 || jQuery(e.target).closest("[data-vrn-inline-zoom]").length) return;
                e.preventDefault();
                isInlinePanning = true;
                inlineStartX = e.clientX - inlinePanX;
                inlineStartY = e.clientY - inlinePanY;
                $section.find("[data-vrn-main-slider]").addClass("is-dragging");
            });

            jQuery(window).on("mousemove", function (e) {
                if (!isInlinePanning || inlineZoomScale <= 1) return;
                inlinePanX = e.clientX - inlineStartX;
                inlinePanY = e.clientY - inlineStartY;
                applyInlineZoomTransform();
            });

            jQuery(window).on("mouseup", function () {
                if (isInlinePanning) {
                    isInlinePanning = false;
                    $section.find("[data-vrn-main-slider]").removeClass("is-dragging");
                }
            });

            $section.on("click", SELECTOR.openGalleryButton, openGallery);
            jQuery(document).on("click", SELECTOR.closeGalleryButton, closeGallery);
            jQuery(document).on("click", "[data-vrn-zoom-in]", function (e) {
                e.preventDefault();
                setZoomScale(currentZoomScale + 0.5);
            });
            jQuery(document).on("click", "[data-vrn-zoom-out]", function (e) {
                e.preventDefault();
                setZoomScale(currentZoomScale - 0.5);
            });
            jQuery(document).on("click", "[data-vrn-zoom-reset]", function (e) {
                e.preventDefault();
                setZoomScale(currentZoomScale > 1 ? 1 : 2);
            });

            // Wheel zoom inside gallery modal
            $galleryModal.on("wheel", ".vr-gallery-modal__image-wrapper", function (e) {
                e.preventDefault();
                const delta = e.originalEvent.deltaY < 0 ? 0.25 : -0.25;
                setZoomScale(currentZoomScale + delta);
            });

            // Drag / pan zoomed image (mouse + single-finger touch) and 2-finger pinch zoom
            let initialPinchDistance = null;
            let initialPinchScale = 1;

            function getTouchDistance(touches) {
                const dx = touches[0].clientX - touches[1].clientX;
                const dy = touches[0].clientY - touches[1].clientY;
                return Math.hypot(dx, dy);
            }

            $galleryModal.on("touchstart", ".vr-gallery-modal__image-wrapper", function (e) {
                const touches = e.originalEvent.touches;
                if (touches && touches.length === 2) {
                    isPanning = false;
                    initialPinchDistance = getTouchDistance(touches);
                    initialPinchScale = currentZoomScale;
                } else if (touches && touches.length === 1 && currentZoomScale > 1) {
                    isPanning = true;
                    startPanX = touches[0].clientX - panX;
                    startPanY = touches[0].clientY - panY;
                }
            });

            $galleryModal.on("touchmove", ".vr-gallery-modal__image-wrapper", function (e) {
                const touches = e.originalEvent.touches;
                if (touches && touches.length === 2 && initialPinchDistance) {
                    e.preventDefault();
                    const newDist = getTouchDistance(touches);
                    const ratio = newDist / initialPinchDistance;
                    setZoomScale(initialPinchScale * ratio);
                } else if (touches && touches.length === 1 && isPanning && currentZoomScale > 1) {
                    e.preventDefault();
                    panX = touches[0].clientX - startPanX;
                    panY = touches[0].clientY - startPanY;
                    applyTransformToActiveModalImage();
                }
            });

            $galleryModal.on("touchend touchcancel", ".vr-gallery-modal__image-wrapper", function () {
                isPanning = false;
                initialPinchDistance = null;
            });

            $galleryModal.on("mousedown", ".vr-gallery-modal__image-wrapper", function (e) {
                if (currentZoomScale <= 1) return;
                e.preventDefault();
                isPanning = true;
                startPanX = e.clientX - panX;
                startPanY = e.clientY - panY;
            });

            jQuery(window).on("mousemove", function (e) {
                if (!isPanning || currentZoomScale <= 1) return;
                panX = e.clientX - startPanX;
                panY = e.clientY - startPanY;
                applyTransformToActiveModalImage();
            });

            jQuery(window).on("mouseup", function () {
                isPanning = false;
            });

            $galleryModal.on("click", function (e) {
                if (jQuery(e.target).hasClass('vr-gallery-modal')) {
                    closeGallery();
                }
            });

            window.addEventListener("popstate", function (event) {
                if (event.state && event.state.path) {
                    switchConfigurationProduct(event.state.path, false);
                }
            });

            // Direct buy button click
            if ($directBuyButton.length) {
                $directBuyButton.on("click", function (e) {
                    e.preventDefault();
                    const $form = $section.find("form").first();
                    if (!$form.length) return;

                    let $returnTo = $form.find('input[name="return_to"]');
                    if (!$returnTo.length) {
                        $returnTo = jQuery('<input type="hidden" name="return_to">');
                        $form.append($returnTo);
                    }
                    $returnTo.val("/checkout");
                    $form.submit();
                });
            }

            // Normal add to cart button click
            $addToCartButton.on("click", function () {
                const $form = $section.find("form").first();
                if (!$form.length) return;

                const $returnTo = $form.find('input[name="return_to"]');
                if ($returnTo.length) {
                    $returnTo.val("");
                }
            });

            // If any other script updates the hidden variant select,
            // keep this section UI (including ATC state) in sync.
            $variantSelect.on("change", function () {
                if (isSyncingHiddenVariantSelect) return;

                const variantFromSelect = findVariantById(jQuery(this).val());
                if (!variantFromSelect) return;
                selectedOptions = variantFromSelect.options.slice();
                syncPickerControls();
                renderVariant(variantFromSelect);
            });

            // Re-apply any URL variant once all resources have loaded
            // (handles cases where other scripts mutate the DOM after DOMContentLoaded).
            jQuery(window).on("load", function () {
                applyVariant(findVariantById(getUrlVariantId()));
            });

            // Video Swiper Slider initialization
            const $videosSlider = $section.find("[data-vrn-videos-slider]");
            const $videoCards = $section.find("[data-video-card]");

            if ($videosSlider.length && typeof Swiper !== "undefined") {
                new Swiper($videosSlider[0], {
                    slidesPerView: 'auto',
                    spaceBetween: 16,
                    grabCursor: true,

                });
            }

            // Video Cards play/pause toggle click logic
            $videoCards.on("click", function () {
                const $card = jQuery(this);
                const $video = $card.find(".vr-video-card__player").first();
                if (!$video.length) return;

                const videoEl = $video[0];

                // Stop all other playing videos in the slider first (premium reels/shorts UX)
                $videoCards.not($card).each(function () {
                    const otherVideo = jQuery(this).find(".vr-video-card__player").first()[0];
                    if (otherVideo && !otherVideo.paused) {
                        otherVideo.pause();
                        jQuery(this).removeClass("is-playing");
                    }
                });

                if (videoEl.paused) {
                    videoEl.play().catch(function (err) {
                        console.error("Playback failed:", err);
                    });
                    $card.addClass("is-playing");
                } else {
                    videoEl.pause();
                    $card.removeClass("is-playing");
                }
            });
        }

        // ─── Bootstrap ────────────────────────────────────────────────────────
        bindEvents();
        initSwiperSliders(); // Initialize Swiper

        const urlVariant = findVariantById(getUrlVariantId());
        if (urlVariant) {
            applyVariant(urlVariant);
        }

    }
})();


// add to cart
$(document).ready(function () {
    $('#vrn-form').on('submit', function (e) {
        e.preventDefault(); // Prevent standard form submission & redirection

        const $form = $(this);
        const $submitBtn = $form.find('[data-vrn-atc]');
        const originalText = $submitBtn.text();

        // Optional: Change button state to loading
        $submitBtn.text('Adding...').prop('disabled', true);

        // Serialize form data (captures variant ID and quantity)
        const formData = $form.serialize();

        $.ajax({
            type: 'POST',
            url: '/cart/add.js',
            data: formData,
            dataType: 'json',
            success: function (product) {
                // Restore button text
                $submitBtn.text('Added!').prop('disabled', false);

                // Fetch updated cart data to refresh your cart drawer UI seamlessly
                $.getJSON('/cart.js', function (cart) {
                    // Refresh your cart drawer contents/badges here
                    // e.g., if you are using the previous cart drawer function:
                    // refreshCartUI(cart); 
                    window.updateGlobalCart(true);
                    // Automatically open your cart drawer


                    $('#cartDrawer').addClass('is-active');
                    $('#cartDrawerOverlay').addClass('is-active');
                    $('body').css('overflow', 'hidden');


                    // Reset button back to normal after 1.5 seconds
                    setTimeout(() => {
                        $submitBtn.text(originalText);
                    }, 1500);
                });
            },
            error: function (jqXHR, textStatus, errorThrown) {
                console.error('Error adding to cart:', errorThrown);
                $submitBtn.text('Error').prop('disabled', false);

                setTimeout(() => {
                    $submitBtn.text(originalText);
                }, 1500);
            }
        });
    });

    // Sticky Add-to-Cart for desktop when main button is scrolled out of view
    const $wrap = $('[data-vrn-atc-wrap]');
    const $btnContainer = $('.action-buttons');

    if ($wrap.length && $btnContainer.length) {
        let ticking = false;

        function checkStickyAtc() {
            if (window.innerWidth < 768) {
                // On mobile, CSS handles fixed position at bottom
                if ($btnContainer.hasClass('is-scrolled-fixed')) {
                    $btnContainer.removeClass('is-scrolled-fixed');
                    $wrap.css('min-height', '');
                }
                return;
            }

            const wrapEl = $wrap[0];
            if (!wrapEl) return;

            const rect = wrapEl.getBoundingClientRect();
            // Fixed header height is ~98px. When the bottom of the button scrolls above this, it is hidden.
            const headerOffset = 98;
            const isHiddenAbove = rect.bottom < headerOffset;

            if (isHiddenAbove) {
                if (!$btnContainer.hasClass('is-scrolled-fixed')) {
                    // Lock wrapper height to prevent any layout jump when button becomes fixed
                    $wrap.css('min-height', $wrap.outerHeight() + 'px');
                    $btnContainer.addClass('is-scrolled-fixed');
                }
            } else {
                if ($btnContainer.hasClass('is-scrolled-fixed')) {
                    $btnContainer.removeClass('is-scrolled-fixed');
                    $wrap.css('min-height', '');
                }
            }
        }

        function onScrollOrResize() {
            if (!ticking) {
                window.requestAnimationFrame(function () {
                    checkStickyAtc();
                    ticking = false;
                });
                ticking = true;
            }
        }

        $(window).on('scroll resize', onScrollOrResize);
        checkStickyAtc();
    }
});

// Thumbnail clicks are centrally managed by handleSlideSelection and onThumbClick inside initSingleSection.


